/**
 * Bhairav chart overlay — body-high/body-low stop-and-reverse on the USER-SELECTED
 * candle type (HA / CLH; any other display type falls back to CLH), now filtered
 * by the PRODUCTION gate ladder so the chart shows exactly what autotrade emits
 * (see ./bhairavGates.ts — mirror of the backend scan's gates):
 *
 *   WEEKLY  raw Bhairav          DAILY  AvgCloud + EMA50 gate
 *   4H      Daily+Weekly Bhairav alignment (needs opts.dailyCandles)
 *   <4H     NO entries — badge explains (autotrade never runs there)
 *
 * Raw rules (unchanged): with bodyHigh = max(open, close), bodyLow = min(open,
 * close) of the transformed candles, per CLOSED bar i vs i−1: BUY on a fresh
 * body-high breakout, SHORT on a fresh body-low breakdown, SELL/COVER when the
 * streak fails; stop-and-reverse; engulfing-body tie-break by candle direction.
 * Gates suppress ENTRIES only — a gate-blocked entry prints as a small grey
 * marker (b/s) so you can see the gate working; exits always run.
 *
 * Trail line: while LONG the exit level for the NEXT bar is this bar's bodyHigh;
 * while SHORT it's this bar's bodyLow. Dashed, NaN gaps while flat.
 *
 * Signals/markers/badge mirror the backend scan (bhairavScan.service +
 * strategies/bhairavGates.ts) — keep the state machines in sync.
 */
import { CandleRow } from '../../components/charts/TradingChart';
import { ChartStrategy, ChartStrategyOptions, StrategyOverlayResult, CandleColor, ChartMarker } from './types';
import { transformCandles } from '../candleTransforms';
import { computeGatedBhairavSteps, bhairavGateProfile, bhairavGateLabel } from './bhairavGates';

const GREEN = '#26a69a';
const RED = '#ef5350';
const NEUTRAL = '#787b86';
const MARK_BUY = '#00C853';
const MARK_SHORT = '#D50000';
const MARK_EXIT = '#FF8F00';
const MARK_BLOCKED = '#9e9e9e';

export type BhairavPos = 'LONG' | 'SHORT' | 'FLAT';

export function computeBhairavOverlay(candles: CandleRow[], opts?: ChartStrategyOptions): StrategyOverlayResult {
  if (!candles || candles.length < 2) return { candleColors: [], markers: [] };
  const ct = opts?.candleType === 'HA' ? 'HA' : 'CLH';
  const tfMin = opts?.ltfMinutes || 0;
  const profile = bhairavGateProfile(tfMin);

  if (profile === 'BLOCKED') {
    // Autotrade parity: no signals below 4H — neutral chart + explanatory notice.
    return {
      candleColors: candles.map(c => ({ time: c.time as number, upColor: NEUTRAL, downColor: NEUTRAL })),
      markers: [],
      notice: 'Bhairav trades 4H / 1D / 1W only — sub-4H entries lose to costs (backtested). Switch the timeframe to see signals.',
    };
  }

  const gated = computeGatedBhairavSteps(candles, ct, tfMin, opts?.dailyCandles);
  const t = transformCandles(candles, ct); // preserves length + bar times
  const bodyHigh = (i: number) => Math.max(Number(t[i].open), Number(t[i].close));
  const bodyLow = (i: number) => Math.min(Number(t[i].open), Number(t[i].close));

  const candleColors: CandleColor[] = [];
  const markers: ChartMarker[] = [];
  const signalsByTime: NonNullable<StrategyOverlayResult['signalsByTime']> = new Map();
  const positionByTime: NonNullable<StrategyOverlayResult['positionByTime']> = new Map();
  const trail: { time: number; value: number }[] = [];

  for (let i = 0; i < gated.steps.length; i++) {
    const st = gated.steps[i];
    const time = st.time;
    switch (st.signal) {
      case 'BUY':
        if (st.reversed) markers.push({ time, position: 'belowBar', text: 'C', color: MARK_EXIT, shape: 'circle' });
        markers.push({ time, position: 'belowBar', text: 'B', color: MARK_BUY, shape: 'arrowUp', size: 2 });
        signalsByTime.set(time, { signal: 'BUY', strength: null, pos: 'LONG', reason: st.reversed ? 'REVERSE' : null });
        break;
      case 'SHORT':
        if (st.reversed) markers.push({ time, position: 'aboveBar', text: 'X', color: MARK_EXIT, shape: 'circle' });
        markers.push({ time, position: 'aboveBar', text: 'S', color: MARK_SHORT, shape: 'arrowDown', size: 2 });
        signalsByTime.set(time, { signal: 'SHORT', strength: null, pos: 'SHORT', reason: st.reversed ? 'REVERSE' : null });
        break;
      case 'SELL':
        markers.push({ time, position: 'aboveBar', text: 'X', color: MARK_EXIT, shape: 'circle' });
        signalsByTime.set(time, { signal: 'EXIT_LONG', strength: null, pos: 'FLAT' });
        break;
      case 'COVER':
        markers.push({ time, position: 'belowBar', text: 'C', color: MARK_EXIT, shape: 'circle' });
        signalsByTime.set(time, { signal: 'EXIT_SHORT', strength: null, pos: 'FLAT' });
        break;
    }
    // Raw entry the production gate suppressed — small grey ghost marker.
    if (st.blockedSignal === 'BUY') markers.push({ time, position: 'belowBar', text: 'b', color: MARK_BLOCKED, shape: 'circle' });
    else if (st.blockedSignal === 'SHORT') markers.push({ time, position: 'aboveBar', text: 's', color: MARK_BLOCKED, shape: 'circle' });

    trail.push({ time, value: st.pos === 'LONG' ? bodyHigh(i) : st.pos === 'SHORT' ? bodyLow(i) : NaN });
    const col = st.pos === 'LONG' ? GREEN : st.pos === 'SHORT' ? RED : NEUTRAL;
    candleColors.push({ time, upColor: col, downColor: col });
    positionByTime.set(time, st.pos);
  }

  const notice = gated.profile === 'H4' && !gated.htfOk
    ? '4H gate needs Daily history (Daily+Weekly alignment) — daily data unavailable, entries suppressed.'
    : undefined;

  return {
    candleColors, markers, signalsByTime, positionByTime, notice,
    lines: [{ name: 'Bhairav trail', data: trail, color: '#ffb300', lineWidth: 1, lineStyle: 2 }],
  };
}

export { bhairavGateLabel, bhairavGateProfile };

export const bhairavStrategy: ChartStrategy = {
  id: 'bhairav',
  name: 'Bhairav',
  computeOverlays(candles: CandleRow[], opts?: ChartStrategyOptions): StrategyOverlayResult {
    return computeBhairavOverlay(candles, opts);
  },
};
